import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { faEarthEurope } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FormControl from "@mui/material/FormControl/FormControl";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select/Select";
import { useTranslation } from "react-i18next";

/**
 * The name of a language, in that language, capitalised for use as a menu item.
 *
 * `Intl.DisplayNames` gives the autonym, which is what we want -- "Deutsch",
 * not "German" -- but it gives it in the language's own orthography, and
 * neither French nor Spanish capitalises language names, so `fr` comes back as
 * "français" and `es` as "español".
 *
 * ICU has a stand-alone context form for exactly this, but `Intl.DisplayNames`
 * does not expose it, so the capital has to be applied here.
 *
 * Deliberately still derived from `Intl` rather than read from a hardcoded
 * name map like the Flutter apps use: this is the one place in the product
 * where adding a language needs no display-name edit, and a fifth copy of that
 * list is the last thing this codebase needs.
 */
function languageLabel(languageCode: string, inLocale?: string): string {
  const locale = inLocale ?? languageCode;
  const raw =
    new Intl.DisplayNames(locale, { type: "language" }).of(languageCode) ??
    languageCode;
  return raw.charAt(0).toLocaleUpperCase(locale) + raw.slice(1);
}

function getValidLanguageCode(i18n: any, availableCodes: string[]): string {
  const browserLang = navigator.language.split("-")[0];
  if (availableCodes.includes(i18n.resolvedLanguage)) {
    return i18n.resolvedLanguage;
  }
  if (availableCodes.includes(i18n.language)) {
    return i18n.language;
  }
  if (availableCodes.includes(browserLang)) {
    return browserLang;
  }
  return "en";
}

export default function LocaleSwitcher() {
  const { i18n } = useTranslation();
  const availableLanguageCodes: string[] = [
    ...Object.keys(i18n.services.resourceStore.data),
    "en", // fallback language
  ];

  const currentLanguage = getValidLanguageCode(i18n, availableLanguageCodes);

  const handleChange = async (event: any) => {
    const newLang = event.target.value ?? "en";

    // i18next does not expose the cause of change in `i18n.language` (user action vs auto-detection,
    // async init, fallback resolution, programmatic changes, etc.)
    //
    // AJV validation library stores error strings in an internal state and is not refreshed when user manually
    // changes locale. Therefore, we need to trigger dynamic form's validation whenever locale is changed manually.
    //
    // Therefore we emit an explicit app-level event at the *source of truth* (the UI action)
    // and consumers can gate their effects on this signal.
    window.dispatchEvent(
      new CustomEvent("skp:manual-locale-change", { detail: { lang: newLang } })
    );

    await i18n.changeLanguage(newLang);
  };

  function renderMenuItem(languageCode: string): JSX.Element {
    return (
      <MenuItem
        value={languageCode}
        key={languageCode}
        color="#747474"
        sx={{
          width: "140px",
          fontSize: "14px",
          paddingLeft: "22px",
          color: "#747474",
        }}
      >
        {languageLabel(languageCode)}
      </MenuItem>
    );
  }

  function renderSelectedValue(value: string): JSX.Element {
    const parsedLocale = new Intl.Locale(value);
    return (
      <div className="flex items-center">
        <div className="flex-shrink">
          <span className="overflow-hidden text-ellipsis whitespace-nowrap">
            {languageLabel(parsedLocale.language, value)}
          </span>
        </div>
        <FontAwesomeIcon
          icon={faEarthEurope}
          className="h-4 w-4 flex-shrink-0 pl-2.5"
        />
      </div>
    );
  }

  return (
    <FormControl>
      <Select
        MenuProps={{
          transformOrigin: { vertical: "top", horizontal: "left" },
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          elevation: 3,
          PaperProps: { sx: { borderRadius: 0 } },
        }}
        renderValue={renderSelectedValue}
        IconComponent={(props) => (
          <FontAwesomeIcon icon={faChevronDown as IconProp} {...props} />
        )}
        autoWidth
        notched={false}
        localeSwitcher
        input={<OutlinedInput localeSwitcher />}
        value={currentLanguage}
        onChange={handleChange}
      >
        {availableLanguageCodes.map(renderMenuItem)}
      </Select>
    </FormControl>
  );
}
